import * as React from 'react';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../../lib/utils';

/**
 * Breadcrumb component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 215:840):
 * - States: Normal (grey-500), Hover (grey-500 + underline), Active (grey-950)
 * - Separator: ChevronRight icon
 * - Optional: Icon prefix
 */
const Breadcrumb = React.forwardRef(({ ...props }, ref) => (
  <nav ref={ref} aria-label="breadcrumb" {...props} />
));
Breadcrumb.displayName = 'Breadcrumb';

const BreadcrumbList = React.forwardRef(({ className, ...props }, ref) => (
  <ol
    ref={ref}
    className={cn(
      'flex flex-wrap items-center gap-1.5 break-words text-sm',
      className
    )}
    {...props}
  />
));
BreadcrumbList.displayName = 'BreadcrumbList';

const BreadcrumbItem = React.forwardRef(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn('inline-flex items-center gap-1.5', className)}
    {...props}
  />
));
BreadcrumbItem.displayName = 'BreadcrumbItem';

const BreadcrumbLink = React.forwardRef(
  ({ asChild, className, active, icon, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a';

    if (active) {
      return (
        <span
          ref={ref}
          className={cn(
            'inline-flex items-center gap-1.5 font-medium text-grey-950 dark:text-grey-50',
            className
          )}
          aria-current="page"
          {...props}
        >
          {props.children}
          {icon && <span className="shrink-0">{icon}</span>}
        </span>
      );
    }

    return (
      <Comp
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1.5 font-medium text-grey-500 transition-colors hover:text-grey-500 hover:underline dark:text-grey-400 dark:hover:text-grey-300',
          className
        )}
        {...props}
      >
        {icon && <span className="shrink-0">{icon}</span>}
        {props.children}
      </Comp>
    );
  }
);
BreadcrumbLink.displayName = 'BreadcrumbLink';

const BreadcrumbPage = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    role="link"
    aria-disabled="true"
    aria-current="page"
    className={cn('font-medium text-grey-950 dark:text-grey-50', className)}
    {...props}
  />
));
BreadcrumbPage.displayName = 'BreadcrumbPage';

const BreadcrumbSeparator = ({ children, className, ...props }) => (
  <li
    role="presentation"
    aria-hidden="true"
    className={cn('[&>svg]:size-3.5 text-grey-400', className)}
    {...props}
  >
    {children ?? <ChevronRight />}
  </li>
);
BreadcrumbSeparator.displayName = 'BreadcrumbSeparator';

const BreadcrumbEllipsis = ({ className, ...props }) => (
  <span
    role="presentation"
    aria-hidden="true"
    className={cn('flex h-9 w-9 items-center justify-center', className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More</span>
  </span>
);
BreadcrumbEllipsis.displayName = 'BreadcrumbElipssis';

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
